<template>
   <LoadingPage v-if="isLoading"></LoadingPage>
   <div class="contracts-page contracts-page-v2 p-3">
      <!-- ── Modern Page Header (Matching Leads V2) ── -->
      <div class="ms-page-header">
         <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
            <div class="d-flex align-items-center gap-3">
               <div class="ms-page-header__icon">
                  <i class="fa-solid fa-file-signature"></i>
               </div>
               <div>
                  <p class="ms-page-header__breadcrumb">Sales Pipeline <span class="ms-breadcrumb-sep">/</span> Contracts</p>
                  <div class="d-flex align-items-center flex-wrap gap-2 mt-1">
                     <h1 class="ms-page-header__title">Contracts Workspace</h1>
                     <span class="badge bg-primary-soft text-primary rounded-pill px-2 py-1 fs-xs fw-bold">
                        {{ pagination.totalItems }} Total
                     </span>
                  </div>
               </div>
            </div>

            <!-- Header Action CTAs -->
            <div class="d-flex align-items-center gap-2 flex-wrap">
               <button 
                  v-if="!$store.state.auth.isConsultant" 
                  type="button" 
                  class="action-btn-circle action-btn-circle--primary"
                  @click="getContractsList(); fetchKpiMetrics();" 
                  :disabled="isLoading"
                  title="Refresh contracts list and signer statuses"
                  aria-label="Refresh"
               >
                  <i class="fa-solid fa-arrows-rotate"></i>
               </button>
            </div>
         </div>
      </div>
      <!-- ── Main Workspace Card ── -->
      <div class="card modern-table-card border-0 shadow-sm">
         <div class="card-body p-3">

            <!-- ── Quick Tabs & Segmented Filters (Matching Leads V2) ── -->
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3 border-bottom mb-3">
               <div class="modern-tabs">
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'all' }" @click="setQuickTab('all')">
                     <i class="fa-solid fa-list me-1"></i> All Contracts
                  </button>
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'signature' }" @click="setQuickTab('signature')">
                     <i class="fa-solid fa-signature me-1"></i> Out for Signature
                     <span v-if="kpiMetrics.outForSignatureCount > 0" class="badge bg-teal-soft text-teal rounded-pill ms-1 fs-xs">{{ kpiMetrics.outForSignatureCount }}</span>
                  </button>
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'executed' }" @click="setQuickTab('executed')">
                     <i class="fa-solid fa-stamp me-1"></i> Executed
                     <span v-if="kpiMetrics.executedCount > 0" class="badge bg-emerald-soft text-emerald rounded-pill ms-1 fs-xs">{{ kpiMetrics.executedCount }}</span>
                  </button>
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'attention' }" @click="setQuickTab('attention')">
                     <i class="fa-solid fa-triangle-exclamation me-1 text-warning"></i> Needs Attention
                     <span v-if="kpiMetrics.attentionCount > 0" class="badge bg-amber-soft text-amber rounded-pill ms-1 fs-xs">{{ kpiMetrics.attentionCount }}</span>
                  </button>
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'draft' }" @click="setQuickTab('draft')">
                     <i class="fa-solid fa-pen-ruler me-1"></i> Drafts
                     <span v-if="kpiMetrics.draftCount > 0" class="badge bg-indigo-soft text-indigo rounded-pill ms-1 fs-xs">{{ kpiMetrics.draftCount }}</span>
                  </button>
               </div>

               <!-- Pagination & View Controls -->
               <div class="d-flex align-items-center gap-2 ms-auto">
                  <div class="d-flex align-items-center gap-1">
                     <span class="text-muted fs-sm">Rows:</span>
                     <select v-model="pagination.pageSize" class="form-select form-select-sm modern-select" style="width: 70px;">
                        <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                     </select>
                  </div>

                  <div class="pagination-modern">
                     <button @click="previousPage()" :disabled="pagination.page === 1" class="pagination-arrow" title="Previous Page">
                        <i class="fa-solid fa-chevron-left"></i>
                     </button>
                     <span class="pagination-label">
                        <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                     </span>
                     <button @click="nextPage()" :disabled="pagination.page === pagination.totalPages || pagination.totalItems === 0" class="pagination-arrow" title="Next Page">
                        <i class="fa-solid fa-chevron-right"></i>
                     </button>
                  </div>
               </div>
            </div>

            <!-- ── Search & Filter Toolbar ── -->
            <div class="d-flex align-items-center flex-wrap gap-2 mb-3">
               <!-- Search Input -->
               <div class="modern-search-wrapper" style="min-width: 260px; max-width: 360px;">
                  <i class="fa-solid fa-magnifying-glass search-icon"></i>
                  <input 
                     v-model.trim="search" 
                     type="text" 
                     class="form-control form-control-sm modern-search-input"
                     placeholder="Search contract title or organisation..." 
                     @keyup="onSearch()" 
                  />
                  <button v-if="search" type="button" class="btn-clear-search" @click="clearSearch()">
                     <i class="fa-solid fa-xmark"></i>
                  </button>
               </div>

               <!-- Status Filter Dropdown -->
               <div class="dropdown">
                  <button 
                     type="button" 
                     class="btn btn-sm modern-filter-btn dropdown-toggle"
                     data-bs-toggle="dropdown" 
                     aria-expanded="false"
                  >
                     <i class="fa-solid fa-sliders me-1 text-teal"></i>
                     <span>Status</span>
                     <span v-if="statusFilter.length > 0" class="filter-count-badge ms-1">
                        {{ statusFilter.length }}
                     </span>
                  </button>
                  <div class="dropdown-menu p-3 shadow-lg border-0" style="min-width: 240px; border-radius: 12px;" @click.stop>
                     <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <span class="fw-bold fs-xs text-muted text-uppercase">Filter Status</span>
                        <button v-if="statusFilter.length > 0" type="button" class="btn btn-link btn-xs p-0 text-teal text-decoration-none" @click="statusFilter = []">Reset</button>
                     </div>
                     <div class="d-flex flex-column gap-2" style="max-height: 250px; overflow-y: auto;">
                        <label v-for="opt in statusOptions" :key="opt.value" class="modern-checkbox-label">
                           <input type="checkbox" :value="opt.value" v-model="statusFilter">
                           <span class="custom-chk"></span>
                           <span class="status-dot ms-2" :class="getContractStatusDotClass(opt.value)"></span>
                           <span class="label-text ms-2">{{ opt.text }}</span>
                        </label>
                     </div>
                  </div>
               </div>

               <!-- Organisation Filter Dropdown -->
               <div class="dropdown">
                  <button 
                     type="button" 
                     class="btn btn-sm modern-filter-btn dropdown-toggle"
                     data-bs-toggle="dropdown" 
                     aria-expanded="false"
                  >
                     <i class="fa-solid fa-building me-1 text-teal"></i>
                     <span>Organisation</span>
                     <span v-if="leadFilter.length > 0" class="filter-count-badge ms-1">
                        {{ leadFilter.length }}
                     </span>
                  </button>
                  <div class="dropdown-menu p-3 shadow-lg border-0" style="min-width: 280px; border-radius: 12px;" @click.stop>
                     <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <span class="fw-bold fs-xs text-muted text-uppercase">Organisations</span>
                        <button v-if="leadFilter.length > 0" type="button" class="btn btn-link btn-xs p-0 text-teal text-decoration-none" @click="leadFilter = []">Reset</button>
                     </div>
                     <div class="d-flex flex-column gap-2" style="max-height: 250px; overflow-y: auto;">
                        <label v-for="(l, idx) in leads" :key="l.id || idx" class="modern-checkbox-label">
                           <input type="checkbox" :value="l.id" v-model="leadFilter">
                           <span class="custom-chk"></span>
                           <span class="label-text ms-2 text-truncate" style="max-width: 210px;">
                              {{ l.organisation_name || `${l.first_name} ${l.last_name}` }}
                           </span>
                        </label>
                     </div>
                  </div>
               </div>

               <!-- Active Filter Chips -->
               <div v-if="isFiltered" class="d-flex align-items-center flex-wrap gap-1 ms-1">
                  <span v-for="val in statusFilter" :key="'status-' + val" class="filter-chip">
                     <span class="status-dot-xs me-1" :class="getContractStatusDotClass(val)"></span>
                     {{ getStatusText(parseInt(val, 10)) }}
                     <i class="fa-solid fa-xmark ms-1 cursor-pointer" @click="removeStatusFilter(val)"></i>
                  </span>
                  <span v-for="val in leadFilter" :key="'lead-' + val" class="filter-chip">
                     <i class="fa-solid fa-building me-1 opacity-75"></i>
                     {{ getLeadDisplayName(val) }}
                     <i class="fa-solid fa-xmark ms-1 cursor-pointer" @click="removeLeadFilter(val)"></i>
                  </span>
                  <button type="button" class="btn btn-link btn-xs p-0 text-teal text-decoration-none ms-1 fw-bold fs-xs" @click="clearAllFiltersAndSearch()">
                     Reset All
                  </button>
               </div>
            </div>

            <!-- ── Main Contracts Table ── -->
            <div class="modern-table-wrapper table-responsive">
               <table class="table modern-table mb-0">
                  <thead>
                     <tr>
                        <th scope="col" style="width: 85px;" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('contract_version') }" @click="toggleSort('contract_version', 'desc')" title="Sort by Version">
                           <div class="d-flex align-items-center gap-1">
                              <span>Version</span>
                              <i :class="getSortIcon('contract_version')"></i>
                           </div>
                        </th>
                        <th scope="col" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('contract_title') }" @click="toggleSort('contract_title', 'asc')" title="Sort by Organisation & Contract Title">
                           <div class="d-flex align-items-center gap-1">
                              <span>Organisation & Contract Title</span>
                              <i :class="getSortIcon('contract_title')"></i>
                           </div>
                        </th>
                        <th scope="col" style="width: 140px; text-align: center;" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('contract_status') }" @click="toggleSort('contract_status', 'asc')" title="Sort by Status">
                           <div class="d-flex align-items-center justify-content-center gap-1">
                              <span>Status</span>
                              <i :class="getSortIcon('contract_status')"></i>
                           </div>
                        </th>
                        <th scope="col" style="width: 250px;" class="text-nowrap">Signers / Parties</th>
                        <th scope="col" style="width: 110px;" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('created_at') }" @click="toggleSort('created_at', 'desc')" title="Sort by Created Date">
                           <div class="d-flex align-items-center gap-1">
                              <span>Created</span>
                              <i :class="getSortIcon('created_at')"></i>
                           </div>
                        </th>
                        <th scope="col" style="width: 110px;" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('sent_at') }" @click="toggleSort('sent_at', 'desc')" title="Sort by Sent Date">
                           <div class="d-flex align-items-center gap-1">
                              <span>Sent Date</span>
                              <i :class="getSortIcon('sent_at')"></i>
                           </div>
                        </th>
                        <th scope="col" style="width: 130px;" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('accepted_at') }" @click="toggleSort('accepted_at', 'desc')" title="Sort by Executed Date">
                           <div class="d-flex align-items-center gap-1">
                              <span>Executed Date</span>
                              <i :class="getSortIcon('accepted_at')"></i>
                           </div>
                        </th>
                        <th scope="col" style="width: 140px; text-align: right;" class="text-nowrap">Actions</th>
                     </tr>
                  </thead>

                  <TableEmptyState
                     v-if="contracts.length === 0"
                     :colspan="6"
                     icon="fa-solid fa-file-signature"
                     :title="hasActiveFilters ? 'No Matching Contracts' : 'No Contracts Yet'"
                     :description="hasActiveFilters
                        ? 'No contracts matched your current search or filter criteria. Try adjusting or clearing your filters.'
                        : 'Contracts generated from accepted proposals will appear here.'"
                     :action-text="hasActiveFilters ? 'Reset All Filters' : ''"
                     :action-icon="hasActiveFilters ? 'fa-solid fa-rotate-left' : ''"
                     @action="hasActiveFilters ? clearAllFilters() : null"
                  />

                  <tbody v-else>
                     <tr 
                        v-for="contract in contracts" 
                        :key="contract.id" 
                        class="modern-row" 
                        @click="openContractInNewTab(contract.id)"
                     >
                        <!-- Version Column with Row Border Indicator and Single Clean Pill -->
                        <td 
                           :class="{
                              'row-border-revision': contract.unresolved_revisions_count && contract.unresolved_revisions_count > 0,
                              'row-border-multi-signer': contract.is_multi_signer && !(contract.unresolved_revisions_count && contract.unresolved_revisions_count > 0)
                           }"
                           :title="getRowIndicatorTooltip(contract)"
                        >
                           <div class="d-flex align-items-center">
                              <span 
                                 class="spark-pill-badge position-relative"
                                 :class="getVersionBadgeClass(contract)"
                                 :title="getVersionTooltip(contract)"
                              >
                                 <i v-if="contract.unresolved_revisions_count && contract.unresolved_revisions_count > 0" class="fa-solid fa-pen-to-square me-1 fs-xxs"></i>
                                 <i v-else-if="contract.is_multi_signer" class="fa-solid fa-users me-1 fs-xxs"></i>
                                 {{ contract.contract_version || 'v1.0' }}
                                 <span v-if="contract.unresolved_revisions_count && contract.unresolved_revisions_count > 0" class="badge-revision-dot"></span>
                              </span>
                           </div>
                        </td>

                        <!-- Organisation & Title -->
                        <td>
                           <div class="d-flex flex-column">
                              <span class="contract-org-name">
                                 {{ getOrganisationDisplayName(contract) }}
                              </span>

                              <div class="contract-sub-title text-muted mt-1">
                                 <i class="fa-solid fa-file-signature me-1 opacity-75"></i>
                                 {{ contract.contract_title || 'Untitled Contract' }}
                              </div>

                              <div 
                                 v-if="contract.Lead && contract.Lead.original_organisation_name && contract.Lead.original_organisation_name !== '' && contract.Lead.original_organisation_name !== getOrganisationDisplayName(contract)" 
                                 class="text-muted fst-italic mt-1 fs-xs"
                              >
                                 <i class="fa-solid fa-clock-rotate-left me-1"></i>Formerly: <strong>{{ contract.Lead.original_organisation_name }}</strong>
                              </div>
                           </div>
                        </td>

                        <!-- Status Pill Badge (Single Clean Soft Pill Badge) -->
                        <td style="text-align: center;">
                           <span 
                              class="spark-pill-badge"
                              :class="getContractStatusBadgeClass(contract.contract_status)"
                           >
                              {{ getStatusText(contract.contract_status) }}
                           </span>
                        </td>

                        <!-- Signers / Parties Column (Person Name & Email, clean Signed/Pending badges) -->
                        <td @click.stop>
                           <div class="d-flex flex-column gap-1 signer-list-wrapper">
                              <div 
                                 v-for="(signer, sIdx) in getContractSignersList(contract)" 
                                 :key="sIdx" 
                                 class="signer-row d-flex align-items-center justify-content-between gap-2"
                                 :title="getSignerTooltip(signer, contract)"
                              >
                                 <div class="d-flex flex-column min-w-0 flex-grow-1" style="line-height: 1.25;">
                                    <span class="signer-name text-truncate fw-semibold text-dark fs-xs">
                                       {{ [signer.first_name || signer.firstName, signer.last_name || signer.lastName].filter(Boolean).join(' ') || signer.name || signer.email }}
                                    </span>
                                    <span v-if="signer.email && signer.name && signer.name !== signer.email" class="signer-email text-truncate text-muted fs-xxs">
                                       {{ signer.email }}
                                    </span>
                                 </div>

                                 <!-- Signer Status Tag -->
                                 <span 
                                    v-if="signer.has_signed" 
                                    class="badge bg-emerald-soft text-emerald rounded-pill px-2 py-1 fs-xxs flex-shrink-0"
                                 >
                                    Signed
                                 </span>
                                 <span 
                                    v-else-if="signer.has_declined" 
                                    class="badge bg-rose-soft text-rose rounded-pill px-2 py-1 fs-xxs flex-shrink-0"
                                 >
                                    Declined
                                 </span>
                                 <span 
                                    v-else-if="contract.contract_status === 9" 
                                    class="badge bg-rose-soft text-rose rounded-pill px-2 py-1 fs-xxs flex-shrink-0"
                                 >
                                    Expired
                                 </span>
                                 <span 
                                    v-else-if="contract.contract_status === 1 || contract.contract_status === 2 || contract.contract_status === 3 || contract.contract_status === 6 || contract.contract_status === 8" 
                                    class="badge bg-amber-soft text-amber rounded-pill px-2 py-1 fs-xxs flex-shrink-0"
                                 >
                                    Pending
                                 </span>
                                 <span 
                                    v-else 
                                    class="badge bg-slate-soft text-secondary rounded-pill px-2 py-1 fs-xxs flex-shrink-0"
                                 >
                                    Draft
                                 </span>
                              </div>

                              <span v-if="getContractSignersList(contract).length === 0" class="text-muted fs-xs">
                                 -
                              </span>
                           </div>
                        </td>

                        <!-- Created Date -->
                        <td>
                           <span class="fs-xs text-secondary text-nowrap">{{ formatDate(contract.created_at) }}</span>
                        </td>

                        <!-- Sent Date -->
                        <td>
                           <span 
                              v-if="isValidDate(contract.sent_at)" 
                              class="fs-xs text-teal fw-medium text-nowrap"
                              :title="formatTimestamp(contract.sent_at)"
                           >
                              {{ formatDate(contract.sent_at) }}
                           </span>
                           <span v-else class="text-muted fs-xs">-</span>
                        </td>

                        <!-- Executed Date -->
                        <td>
                           <span 
                              v-if="isValidDate(contract.executed_at)" 
                              class="fs-xs text-emerald fw-medium text-nowrap"
                              :title="formatTimestamp(contract.executed_at)"
                           >
                              {{ formatDate(contract.executed_at) }}
                           </span>
                           <span v-else class="text-muted fs-xs">-</span>
                        </td>

                        <!-- Action Column (Circular Icon Buttons + Circular 3-Dot More Actions) -->
                        <td style="text-align: right;" @click.stop>
                           <div class="d-flex align-items-center justify-content-end gap-2">
                              <!-- 1) Primary CTA: Send Contract -->
                              <button 
                                 v-if="!$store.state.auth.isGeneralUser" 
                                 type="button" 
                                 class="action-btn-circle action-btn-circle--send position-relative"
                                 :class="{ 'action-btn-circle--unsent': contract.has_revision_not_sent || contract.has_unsent_changes }"
                                 :disabled="contract.contract_status == 4 || contract.contract_status == 5 || contract.contract_status == 8"
                                 :title="getSendButtonTitle(contract)"
                                 @click="onButtonClick('send', contract); openSendModal();"
                              >
                                 <i class="fa-solid fa-paper-plane"></i>
                                 <span v-if="contract.has_revision_not_sent || contract.has_unsent_changes" class="badge-unsent-dot" title="Unsent edits pending"></span>
                              </button>

                              <!-- 2) Secondary CTA: Edit Contract -->
                              <button 
                                 type="button" 
                                 class="action-btn-circle action-btn-circle--edit position-relative"
                                 title="Edit Contract"
                                 @click="onButtonClick('view-edit', contract.id)"
                              >
                                 <i class="fa-solid fa-pen-to-square"></i>
                                 <span v-if="unreadContractIds.includes(contract.id) || contract.has_unread_comments || (contract.unresolved_revisions_count && contract.unresolved_revisions_count > 0)" class="notification-dot" title="Unread comments / revisions"></span>
                              </button>

                              <!-- 3) 3-Dot More Actions Menu -->
                              <div class="dropdown">
                                 <button type="button" class="btn-more-actions" data-bs-toggle="dropdown" aria-expanded="false" title="More Actions">
                                    <i class="fa-solid fa-ellipsis-vertical"></i>
                                 </button>
                                 <ul class="dropdown-menu dropdown-menu-end shadow-lg border-0 py-2 modern-action-dropdown">

                                    <!-- View Client Landing Page -->
                                    <li>
                                       <a class="dropdown-item d-flex align-items-center gap-2 cursor-pointer"
                                          @click="onButtonClick('view-client', contract)">
                                          <i class="fa-solid fa-arrow-up-right-from-square text-info"></i> View Client Page
                                       </a>
                                    </li>

                                    <!-- Activity Log -->
                                    <li>
                                       <a class="dropdown-item d-flex align-items-center gap-2 cursor-pointer"
                                          @click="onButtonClick('activity-log', contract.id)">
                                          <i class="fa-solid fa-clock-rotate-left text-secondary"></i> Activity History
                                       </a>
                                    </li>

                                    <li><hr class="dropdown-divider my-1"></li>

                                    <!-- Cancel Contract -->
                                    <li v-if="!$store.state.auth.isGeneralUser">
                                       <a class="dropdown-item d-flex align-items-center gap-2 text-danger"
                                          :class="{ 'disabled text-muted opacity-50': contract.contract_status == 4 || contract.contract_status == 5, 'cursor-pointer': contract.contract_status != 4 && contract.contract_status != 5 }"
                                          @click="(contract.contract_status != 4 && contract.contract_status != 5) ? onButtonClick('cancel', contract.id) : null">
                                          <i class="fa-solid fa-ban"></i> Cancel Contract
                                       </a>
                                    </li>
                                 </ul>
                              </div>
                           </div>
                        </td>
                     </tr>
                  </tbody>
               </table>
            </div>

         </div>
      </div>

      <!-- ── Modals ── -->

      <!-- Send Contract Modal -->
      <div 
         class="modal fade" 
         id="sendContractModalV2" 
         tabindex="-1" 
         data-bs-keyboard="false" 
         data-bs-backdrop="static" 
         ref="sendFormRef"
         style="top: 2%;"
      >
         <div ref="modalSendFormDialogRef" class="modal-dialog modal-dialog-scrollable modal-lg">
            <div class="modal-content shadow-lg border-0" style="border-radius: 16px; background-color: #fff;">
               <div class="modal-header border-0 pb-0 justify-content-end" style="background-color: transparent;">
                  <button ref="closeSendFormModal" type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
               </div>
               <div class="modal-body p-4 pt-0">
                  <SendForm 
                     ref="sendContractFormRef" 
                     :initialSendTo="contract_signer_emails" 
                     :contractID="contract_id"
                     :initialContractTitle="contract_title"
                     :contractData="selectedContract"
                     @contractSentSuccess="init(); $refs.closeSendFormModal.click(); getContractsList()" 
                     @close="$refs.closeSendFormModal.click()"
                  />
               </div>
            </div>
         </div>
      </div>

      <!-- Activity Log Modal -->
      <div class="modal fade" id="contractActivityModalV2" ref="activityLogModalRef" tabindex="-1">
         <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg modal-blur">
            <div class="modal-content modern-modal-content">
               <div class="modern-activity-modal-header">
                  <div class="d-flex align-items-center gap-3">
                     <div class="modal-header-icon-badge">
                        <i class="fa-solid fa-clock-rotate-left text-primary"></i>
                     </div>
                     <div>
                        <h5 class="modal-title mb-0 fw-bold">Contract Activity Timeline</h5>
                        <p class="modal-subtitle text-muted mb-0 small">
                           Real-time audit log of all events for this contract
                        </p>
                     </div>
                  </div>
                  <button type="button" class="modern-modal-close-btn" data-bs-dismiss="modal" aria-label="Close">
                     <i class="fa-solid fa-xmark"></i>
                  </button>
               </div>
               <div class="modal-body modern-activity-modal-body p-0">
                  <ContractActivity ref="contractActivityRef" />
               </div>
               <div class="modal-footer modern-activity-modal-footer">
                  <button type="button" class="btn btn-light btn-sm fw-semibold rounded-pill px-3" data-bs-dismiss="modal">
                     Close
                  </button>
               </div>
            </div>
         </div>
      </div>

      <!-- Confirmation Modal -->
      <ConfirmModal
         v-model="showConfirmModal"
         :title="confirmModalTitle"
         :message="confirmModalMessage"
         @confirm="onConfirmAction"
      />
   </div>
</template>

<script>
import moment from "moment";
import { debounce } from '@/plugins/helper.js';
import LoadingPage from '@/components/Utilities/vertex-loading.vue';
import SendForm from '@/components/Contracts/SendForm.vue';
import { Modal } from 'bootstrap';
import ContractActivity from '@/components/Contracts/ContractActivity.vue';
import TableEmptyState from '@/components/Utilities/TableEmptyState.vue';

export default {
   name: "ContractsDashboardV2",
   components: {
      LoadingPage,
      SendForm,
      ContractActivity,
      TableEmptyState
   },

   data() {
      return {
         search: "",
         leads: [],
         contracts: [],
         unreadContractIds: [],
         pagination: {
            pageSize: 10,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         rowsPerPageList: [
            { text: "5", value: 5 },
            { text: "10", value: 10 },
            { text: "20", value: 20 },
            { text: "50", value: 50 },
         ],
         statusFilter: ["0", "1", "2", "3", "4", "6", "7", "8", "9"],
         leadFilter: [],
         activeQuickTab: 'all',
         statusOptions: [
            { text: "Draft", value: "0" },
            { text: "Sent", value: "1" },
            { text: "Viewed", value: "2" },
            { text: "In Review", value: "3" },
            { text: "Executed", value: "4" },
            { text: "Cancelled", value: "5" },
            { text: "Pending Approval", value: "6" },
            { text: "Approved", value: "7" },
            { text: "Declined", value: "8" },
            { text: "Expired", value: "9" },
         ],
         sorting: {
            field: "created_at",
            order: "desc",
         },
         isLoading: false,
         contract_signer_emails: [],
         contract_client_email: "",
         contract_id: "",
         contract_title: "",
         selectedContract: null,
         sendContractModalInstance: null,
         activityLogModalInstance: null,
         showConfirmModal: false,
         confirmModalTitle: '',
         confirmModalMessage: '',
         pendingAction: null,
         pendingActionData: null,
         kpiMetrics: {
            totalContracts: 0,
            outForSignatureCount: 0,
            executedCount: 0,
            attentionCount: 0,
            draftCount: 0,
         }
      };
   },

   created() {
      this.init();
      this.debounceFn = debounce(() => this.getContractsList(), 500);
   },

   mounted() {
      this.sendContractModalInstance = new Modal(this.$refs.sendFormRef);
      this.activityLogModalInstance = new Modal(this.$refs.activityLogModalRef);

      window.addEventListener('contract-signed', this.handleContractSignedEvent);
   },

   beforeUnmount() {
      window.removeEventListener('contract-signed', this.handleContractSignedEvent);
   },

   computed: {
      isFiltered() {
         return (this.search && this.search.trim().length > 0) ||
                this.statusFilter.length > 0 ||
                this.leadFilter.length > 0 ||
                this.activeQuickTab !== 'all';
      },
      hasActiveFilters() {
         return this.isFiltered;
      },
      paginationLabel() {
         const start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems);
         const end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems);
         return start + " - " + end;
      },
   },

   watch: {
      'pagination.pageSize'() {
         this.pagination.page = 1;
         this.getContractsList();
      },
      'pagination.page'() {
         this.getContractsList();
      },
      statusFilter: {
         handler() {
            this.pagination.page = 1;
            this.getContractsList();
         },
         deep: true
      },
      leadFilter: {
         handler() {
            this.pagination.page = 1;
            this.getContractsList();
         },
         deep: true
      },
   },

   methods: {
      async init() {
         await this.getLeadsList();
         this.getContractsList();
         this.fetchKpiMetrics();
      },

      setQuickTab(tab) {
         this.activeQuickTab = tab;
         this.pagination.page = 1;
         if (tab === 'all') {
            this.statusFilter = ["0", "1", "2", "3", "4", "6", "7", "8", "9"];
         } else if (tab === 'signature') {
            this.statusFilter = ["1", "2", "3", "6"];
         } else if (tab === 'executed') {
            this.statusFilter = ["4", "7"];
         } else if (tab === 'attention') {
            this.statusFilter = ["5", "8", "9"];
         } else if (tab === 'draft') {
            this.statusFilter = ["0"];
         }
         this.getContractsList();
      },

      async fetchKpiMetrics() {
         try {
            const res = await this.$axios.get('/contracts?pageSize=500&page=1');
            const allContracts = res.data.vertex_contracts || [];
            
            let outForSig = 0;
            let executed = 0;
            let attention = 0;
            let drafts = 0;

            allContracts.forEach(c => {
               const st = Number(c.contract_status);
               if (st === 0) drafts++;
               if (st === 1 || st === 2 || st === 3 || st === 6) outForSig++;
               if (st === 4 || st === 7) executed++;
               if (st === 5 || st === 8 || st === 9 || c.has_revision_not_sent || c.has_unsent_changes || (c.unresolved_revisions_count && c.unresolved_revisions_count > 0)) {
                  attention++;
               }
            });

            this.kpiMetrics = {
               totalContracts: res.data.total_items || allContracts.length,
               outForSignatureCount: outForSig,
               executedCount: executed,
               attentionCount: attention,
               draftCount: drafts,
            };
         } catch (err) {
            console.error("Error fetching KPI metrics for contracts:", err);
         }
      },

      isSorted(field) {
         if (field === 'created_at') return this.sorting.field === 'created_at' || this.sorting.field === 'id';
         if (field === 'contract_title') return this.sorting.field === 'contract_title' || this.sorting.field === 'title';
         if (field === 'contract_status') return this.sorting.field === 'contract_status' || this.sorting.field === 'status';
         if (field === 'contract_version') return this.sorting.field === 'contract_version' || this.sorting.field === 'version';
         return this.sorting.field === field;
      },

      getSortIcon(field) {
         if (!this.isSorted(field)) {
            return 'fa-solid fa-sort sort-icon-inactive';
         }
         return this.sorting.order === 'asc' ? 'fa-solid fa-sort-up sort-icon-active' : 'fa-solid fa-sort-down sort-icon-active';
      },

      toggleSort(field, defaultOrder = 'asc') {
         if (this.isSorted(field)) {
            this.sorting.order = this.sorting.order === 'asc' ? 'desc' : 'asc';
         } else {
            this.sorting.field = field;
            this.sorting.order = defaultOrder;
         }
         this.pagination.page = 1;
         this.getContractsList();
      },

      async getContractsList() {
         this.isLoading = true;
         let apiPath = "/contracts?pageSize=" + this.pagination.pageSize + "&page=" + this.pagination.page;

         if (this.sorting.field) {
            apiPath += "&sort=" + this.sorting.field;
            if (this.sorting.order !== "") {
               apiPath += "&order=" + this.sorting.order;
            }
         }

         if (this.search !== "") {
            apiPath += "&search=" + encodeURIComponent(this.search);
         }

         if (this.statusFilter && this.statusFilter.length > 0) {
            apiPath += "&status=" + this.statusFilter.join(",");
         }

         if (this.leadFilter && this.leadFilter.length > 0) {
            apiPath += "&lead=" + encodeURIComponent(this.leadFilter.join(","));
         }

         try {
            const response = await this.$axios.get(apiPath);
            this.contracts = response.data.vertex_contracts || [];
            this.unreadContractIds = response.data.unread_comment_contract_ids || [];
            this.pagination.totalItems = response.data.total_items;
            this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.pagination.pageSize) || 1;
         } catch (error) {
            this.$toast.error(error.response?.data?.message || "Failed to fetch contracts");
         } finally {
            this.isLoading = false;
         }
      },

      onSearch() {
         this.pagination.page = 1;
         this.debounceFn();
      },

      clearSearch() {
         this.search = '';
         this.pagination.page = 1;
         this.getContractsList();
      },

      clearAllFiltersAndSearch() {
         this.search = '';
         this.statusFilter = ["0", "1", "2", "3", "4", "6", "7", "8", "9"];
         this.leadFilter = [];
         this.activeQuickTab = 'all';
         this.pagination.page = 1;
         this.getContractsList();
      },

      clearAllFilters() {
         this.clearAllFiltersAndSearch();
      },

      removeStatusFilter(val) {
         this.statusFilter = this.statusFilter.filter(v => v !== val);
      },

      removeLeadFilter(val) {
         this.leadFilter = this.leadFilter.filter(v => v !== val);
      },

      getOrganisationDisplayName(contract) {
         if (contract.auth_organisation && contract.auth_organisation.name) {
            return contract.auth_organisation.name;
         }
         if (contract.Lead && contract.Lead.organisation_name) {
            return contract.Lead.organisation_name;
         }
         return "No Org Name Set";
      },

      getLeadDisplayName(uuid) {
         if (!this.leads) return uuid;
         const lead = this.leads.find(l => l.id === uuid || l.uuid === uuid);
         if (!lead) return uuid;
         return lead.organisation_name || `${lead.first_name} ${lead.last_name}`;
      },

      async getLeadsList() {
         try {
            const res = await this.$axios.get("/leads?pageSize=200");
            this.leads = res.data.leads || [];
         } catch (err) {
            console.error("Failed to load leads", err);
         }
      },

      getContractSignersList(contract) {
         let details = [];

         // 1. Pull signers from contract_acceptance
         if (contract.contract_acceptance && contract.contract_acceptance.signers && contract.contract_acceptance.signers.length > 0) {
            contract.contract_acceptance.signers.forEach(s => {
               const hasSigned = !!(s.has_signed || s.signed_at);
               const first = s.first_name || s.firstName || '';
               const last = s.last_name || s.lastName || '';
               const name = [first, last].filter(Boolean).join(' ') || s.name || '';
               details.push({
                  first_name: first,
                  last_name: last,
                  firstName: first,
                  lastName: last,
                  name: name,
                  email: s.email,
                  has_signed: hasSigned,
                  has_declined: !!(s.has_declined || s.declined_at),
                  signed_at: s.signed_at
               });
            });
         }
         // 2. Fallback to shared users
         if (details.length === 0 && contract.shared_users && contract.shared_users.length > 0) {
            contract.shared_users.forEach(su => {
               if (su.share_type === 'signer' && su.email) {
                  const hasSigned = !!(contract.is_accepted || contract.contract_status === 4 || contract.contract_status === 7);
                  const first = su.first_name || su.firstName || '';
                  const last = su.last_name || su.lastName || '';
                  const name = [first, last].filter(Boolean).join(' ') || su.signer_name || '';
                  details.push({
                     first_name: first,
                     last_name: last,
                     firstName: first,
                     lastName: last,
                     name: name,
                     email: su.email,
                     has_signed: hasSigned,
                     signed_at: hasSigned ? contract.executed_at : null
                  });
               }
            });
         }
         // 3. Fallback to Lead
         if (details.length === 0 && contract.Lead && contract.Lead.email) {
            const hasSigned = !!(contract.is_accepted || contract.contract_status === 4 || contract.contract_status === 7);
            const first = contract.Lead.first_name || '';
            const last = contract.Lead.last_name || '';
            const leadPersonName = [first, last].filter(Boolean).join(' ') || contract.Lead.name || '';
            details.push({
               first_name: first,
               last_name: last,
               firstName: first,
               lastName: last,
               name: leadPersonName,
               email: contract.Lead.email,
               has_signed: hasSigned,
               signed_at: hasSigned ? contract.executed_at : null
            });
         }
         return details;
      },

      getSignerTooltip(signer, contract) {
         const formattedName = [signer.first_name || signer.firstName, signer.last_name || signer.lastName].filter(Boolean).join(' ') || signer.name;
         const displayName = formattedName ? `${formattedName} (${signer.email})` : (signer.email || 'Signer');
         if (signer.has_signed) {
            const dateStr = this.isValidDate(signer.signed_at) 
               ? ` on ${this.formatTimestamp(signer.signed_at)}` 
               : (this.isValidDate(contract.executed_at) ? ` on ${this.formatTimestamp(contract.executed_at)}` : '');
            return `Signed by ${displayName}${dateStr}`;
         }
         if (signer.has_declined) {
            return `Declined by ${displayName}`;
         }
         if (contract.contract_status === 9) {
            return `${displayName} — Contract expired before signature`;
         }
         if (contract.contract_status === 1 || contract.contract_status === 2 || contract.contract_status === 3 || contract.contract_status === 6 || contract.contract_status === 8) {
            return `Waiting for signature from ${displayName}`;
         }
         return `${displayName} — Not sent yet`;
      },

      isValidDate(date) {
         if (!date) return false;
         const m = moment(date);
         return m.isValid() && m.year() >= 1980;
      },

      formatDate(date) {
         if (!this.isValidDate(date)) return "-";
         return moment(date).format("DD-MMM-YYYY");
      },

      formatTimestamp(date) {
         if (!this.isValidDate(date)) return "-";
         return moment(date).format("DD-MMM-YYYY @ HH:mm:ss");
      },

      previousPage() {
         if (this.pagination.page > 1) this.pagination.page--;
      },

      nextPage() {
         if (this.pagination.page < this.pagination.totalPages) this.pagination.page++;
      },

      getContractStatusBadgeClass(statusCode) {
         switch (Number(statusCode)) {
            case 1: return 'spark-pill-badge--sent';
            case 2: return 'spark-pill-badge--viewed';
            case 3: return 'spark-pill-badge--review';
            case 4: return 'spark-pill-badge--executed';
            case 5: return 'spark-pill-badge--cancelled';
            case 6: return 'spark-pill-badge--pending-approval';
            case 7: return 'spark-pill-badge--approved';
            case 8: return 'spark-pill-badge--declined';
            case 9: return 'spark-pill-badge--expired';
            default: return 'spark-pill-badge--draft';
         }
      },

      getContractStatusIcon(statusCode) {
         switch (Number(statusCode)) {
            case 1: return 'fa-solid fa-paper-plane';
            case 2: return 'fa-solid fa-eye';
            case 3: return 'fa-solid fa-clock-rotate-left';
            case 4: return 'fa-solid fa-stamp';
            case 5: return 'fa-solid fa-ban';
            case 6: return 'fa-solid fa-user-clock';
            case 7: return 'fa-solid fa-circle-check';
            case 8: return 'fa-solid fa-circle-xmark';
            case 9: return 'fa-solid fa-calendar-xmark';
            default: return 'fa-solid fa-pen';
         }
      },

      getContractStatusDotClass(statusCode) {
         switch (Number(statusCode)) {
            case 1:
            case 2:
            case 3:
            case 6: return 'status-dot--teal';
            case 4:
            case 7: return 'status-dot--emerald';
            case 5:
            case 8:
            case 9: return 'status-dot--rose';
            default: return 'status-dot--indigo';
         }
      },

      getStatusText(statusCode) {
         switch (Number(statusCode)) {
            case 1: return "Sent";
            case 2: return "Viewed";
            case 3: return "In Review";
            case 4: return "Executed";
            case 5: return "Cancelled";
            case 6: return "Pending Approval";
            case 7: return "Approved";
            case 8: return "Declined";
            case 9: return "Expired";
            default: return "Draft";
         }
      },

      getVersionBadgeClass(contract) {
         if (contract.unresolved_revisions_count && contract.unresolved_revisions_count > 0) {
            return 'spark-pill-badge--revision-pending';
         }
         if (contract.is_multi_signer) {
            return 'spark-pill-badge--multi-signer';
         }
         const versionNumber = contract.contract_version;
         if (!versionNumber) return 'spark-pill-badge--version-major';
         const clean = String(versionNumber).startsWith('v') ? String(versionNumber).slice(1) : String(versionNumber);
         const parts = clean.split('.');
         if (parts.length >= 2) {
            const minor = parseInt(parts[1], 10);
            if (!isNaN(minor)) {
               return minor > 0 ? 'spark-pill-badge--version-minor' : 'spark-pill-badge--version-major';
            }
         }
         return 'spark-pill-badge--version-major';
      },

      getVersionTooltip(contract) {
         if (contract.unresolved_revisions_count && contract.unresolved_revisions_count > 0) {
            return `Version ${contract.contract_version || '1.0'} (${contract.unresolved_revisions_count} unresolved client revision${contract.unresolved_revisions_count > 1 ? 's' : ''})`;
         }
         if (contract.is_multi_signer) {
            return `Version ${contract.contract_version || '1.0'} — Multi-Signer Contract`;
         }
         return `Version ${contract.contract_version || '1.0'}`;
      },

      getRowIndicatorTooltip(contract) {
         if (contract.unresolved_revisions_count && contract.unresolved_revisions_count > 0) {
            return `${contract.unresolved_revisions_count} Unresolved client revision${contract.unresolved_revisions_count > 1 ? 's' : ''} requested`;
         }
         if (contract.is_multi_signer) {
            return 'Multi-Signer Contract';
         }
         return '';
      },

      getSendButtonTitle(contract) {
         if (contract.contract_status == 4) return 'Contract fully executed';
         if (contract.contract_status == 5) return 'Contract cancelled';
         if (contract.contract_status == 8) return 'Contract declined';
         if (contract.contract_status == 9) return 'Contract expired — click to resend';
         if (contract.has_revision_not_sent || contract.has_unsent_changes) return 'Contract has unsent edits. Click to send to client.';
         return 'Send Contract';
      },

      openContractInNewTab(id) {
         const routeData = this.$router.resolve({ name: 'contract-view', params: { id: id } });
         window.open(routeData.href, '_blank');
      },

      viewClientContract(contract) {
         if (contract && contract.unique_key && contract.internal_key) {
            const routeData = this.$router.resolve({ name: 'clientContract', params: { unique_key: contract.unique_key, access_key: contract.internal_key } });
            window.open(routeData.href, '_blank');
         } else {
            this.$toast.error("Could not generate client link. Contract data is missing required keys.");
         }
      },

      openSendModal() {
         this.sendContractModalInstance.show();
      },

      openCreateContractModal() {
         this.$router.push({ name: 'contract-create' });
      },

      onButtonClick(action, data) {
         if (action === "send") {
            this.selectedContract = data;
            this.contract_signer_emails = this.getContractSignersList(data);
            this.contract_client_email = data.Lead?.email || '';
            this.contract_id = data.uuid || data.id;
            this.contract_title = data.contract_title;
            this.$nextTick(() => {
               if (this.$refs.sendContractFormRef) {
                  this.$refs.sendContractFormRef.handleContractData(data);
                  if (this.contract_signer_emails && this.contract_signer_emails.length > 0) {
                     this.$refs.sendContractFormRef.updateInitialSendTo(this.contract_signer_emails);
                  }
               }
            });
         } else if (action === "view-edit") {
            this.$router.push({ name: 'contract-view', params: { id: data } });
         } else if (action === "view-client") {
            this.viewClientContract(data);
         } else if (action === "activity-log") {
            this.$refs.contractActivityRef.init(data);
            this.activityLogModalInstance.show();
         } else if (action === "cancel") {
            this.cancelContractConfirm(data);
         }
      },

      cancelContractConfirm(contractId) {
         this.pendingAction = 'cancelContract';
         this.pendingActionData = contractId;
         this.confirmModalTitle = 'Cancel Contract';
         this.confirmModalMessage = 'Are you sure you want to cancel this contract? Once cancelled, clients will no longer be able to sign.';
         this.showConfirmModal = true;
      },

      async onConfirmAction() {
         const action = this.pendingAction;
         const data = this.pendingActionData;
         this.pendingAction = null;
         this.pendingActionData = null;

         if (action === 'cancelContract') {
            this.isLoading = true;
            try {
               const response = await this.$axios.post(`/contracts/${data}/cancel`);
               if (response.data.success) {
                  this.$toast.success(response.data.message || "Contract cancelled successfully");
                  this.getContractsList();
                  this.fetchKpiMetrics();
               } else {
                  this.$toast.error(response.data.message);
               }
            } catch (error) {
               this.$toast.error(error.response?.data?.message || "Failed to cancel contract");
            } finally {
               this.isLoading = false;
            }
         }
      },

      handleContractSignedEvent() {
         this.getContractsList();
         this.fetchKpiMetrics();
      }
   }
}
</script>

<style scoped>
/* ── Contracts V2 Workspace Styling (Exact match to Leads V2) ── */
.contracts-page-v2 {
   color: #1e293b;
   font-family: inherit;
}

.fs-xs { font-size: 0.75rem; }
.fs-sm { font-size: 0.85rem; }
.fw-500 { font-weight: 500; }

.bg-teal { background-color: #00A0C1 !important; }
.text-teal { color: #00A0C1 !important; }
.bg-teal-soft { background-color: rgba(0, 160, 193, 0.1) !important; }

.text-blue { color: #3b82f6 !important; }
.bg-blue-soft { background-color: rgba(59, 130, 246, 0.1) !important; }

.text-emerald { color: #10b981 !important; }
.bg-emerald-soft { background-color: rgba(16, 185, 129, 0.1) !important; }

.text-amber { color: #d97706 !important; }
.text-amber-muted { color: #b45309 !important; font-size: 0.75rem; }
.bg-amber-soft { background-color: rgba(245, 158, 11, 0.1) !important; }

.text-indigo { color: #6366f1 !important; }
.bg-indigo-soft { background-color: rgba(99, 102, 241, 0.1) !important; }

.text-rose { color: #e11d48 !important; }
.bg-rose-soft { background-color: rgba(239, 68, 68, 0.1) !important; }

.bg-primary-soft {
   background-color: rgba(0, 160, 193, 0.1) !important;
   color: #008298 !important;
}

/* ── Page Header Styles ── */
.ms-page-header {
   margin-bottom: 20px;
   padding: 18px 22px;
   background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
   border: 1px solid rgba(0, 130, 152, 0.15);
   border-left: 5px solid #008298;
   border-radius: 12px;
   box-shadow: 0 4px 16px rgba(0, 78, 90, 0.04);
}

.ms-page-header__content {
   display: flex;
   align-items: center;
   gap: 16px;
}

.ms-page-header__icon {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: rgba(0, 130, 152, 0.08);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.15rem;
   color: #008298;
}

.ms-page-header__title {
   font-size: 1.45rem;
   font-weight: 800;
   color: #00363f;
   margin: 0;
   letter-spacing: -0.3px;
}

.ms-page-header__breadcrumb {
   font-size: 0.76rem;
   color: #557d84;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.8px;
   margin-bottom: 4px;
}

.ms-breadcrumb-sep {
   margin: 0 6px;
   color: rgba(0, 130, 152, 0.3);
}

.modern-action-btn {
   height: 38px;
   padding: 0 16px;
   font-size: 0.85rem;
   font-weight: 600;
   border-radius: 8px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   transition: all 0.2s ease;
}

.btn-ghost-secondary {
   background: #ffffff;
   border: 1px solid #cbd5e1;
   color: #334155;
}
.btn-ghost-secondary:hover:not(:disabled) {
   background: #f8fafc;
   border-color: #00A0C1;
   color: #00A0C1;
}

/* ── Modern Table Card & Workspace ── */
.modern-table-card {
   border-radius: 14px;
   background: #ffffff;
}

/* ── Modern Tabs ── */
.modern-tabs {
   display: flex;
   gap: 6px;
   flex-wrap: wrap;
}

.modern-tab {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   border-radius: 8px;
   padding: 6px 14px;
   font-size: 0.85rem;
   font-weight: 600;
   color: #64748b;
   cursor: pointer;
   transition: all 0.2s ease;
   display: inline-flex;
   align-items: center;
}

.modern-tab:hover {
   background: #f1f5f9;
   color: #334155;
}

.modern-tab.active {
   background: #00A0C1;
   color: #ffffff;
   border-color: #00A0C1;
   box-shadow: 0 2px 6px rgba(0, 160, 193, 0.25);
}

.modern-tab.active i {
   color: #ffffff !important;
}

.modern-tab.active .badge {
   background-color: rgba(255, 255, 255, 0.25) !important;
   color: #ffffff !important;
}

/* ── Search & Filter Omnibar ── */
.modern-search-wrapper {
   position: relative;
   display: flex;
   align-items: center;
}

.search-icon {
   position: absolute;
   left: 12px;
   color: #94a3b8;
   font-size: 0.85rem;
   pointer-events: none;
}

.modern-search-input {
   height: 38px;
   border-radius: 8px;
   border: 1px solid #cbd5e1;
   padding-left: 36px;
   padding-right: 32px;
   font-size: 0.85rem;
   transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.modern-search-input:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.btn-clear-search {
   position: absolute;
   right: 8px;
   background: none;
   border: none;
   color: #94a3b8;
   font-size: 0.85rem;
   cursor: pointer;
   padding: 4px;
}

.btn-clear-search:hover { color: #475569; }

.modern-filter-btn {
   height: 38px;
   border-radius: 8px;
   border: 1px solid #cbd5e1;
   background: #ffffff;
   color: #334155;
   font-weight: 600;
   font-size: 0.85rem;
   padding: 0 12px;
   display: inline-flex;
   align-items: center;
   transition: all 0.2s ease;
}

.modern-filter-btn:hover,
.modern-filter-btn[aria-expanded="true"] {
   border-color: #00A0C1;
   background: #f8fafc;
   color: #00A0C1;
}

.modern-filter-btn.has-active {
   background: #e6f7fa;
   border-color: #00A0C1;
   color: #008298;
}

.filter-count-badge {
   background: #00A0C1;
   color: #ffffff;
   font-size: 0.7rem;
   font-weight: 700;
   padding: 1px 6px;
   border-radius: 8px;
}

.modern-select {
   border-radius: 8px;
   border: 1px solid #cbd5e1;
   height: 34px;
   font-size: 0.8rem;
   font-weight: 500;
   color: #334155;
}

.modern-select:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.15);
}

.filter-chip {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   background: #e6f7fa;
   color: #008298;
   border: 1px solid #b3e5ef;
   padding: 3px 10px;
   border-radius: 16px;
   font-size: 0.75rem;
   font-weight: 600;
}

.filter-chip i {
   font-size: 0.7rem;
   transition: color 0.15s;
}

.filter-chip i:hover { color: #ef4444; }

/* ── Modern Table Data Grid ── */
.modern-table-wrapper {
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   background: #ffffff;
}

.modern-table {
   border-collapse: separate;
   border-spacing: 0;
   width: 100%;
}

.modern-table thead th {
   background-color: #f8fafc;
   color: #475569;
   font-weight: 700;
   font-size: 0.78rem;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   padding: 12px 16px;
   border-bottom: 1.5px solid #e2e8f0;
   white-space: nowrap !important;
}

/* ── Sortable Column Headers ── */
.sortable-th {
   cursor: pointer;
   user-select: none;
   transition: background-color 0.15s ease, color 0.15s ease;
}

.sortable-th:hover {
   background-color: #f1f5f9 !important;
   color: #00A0C1 !important;
}

.sortable-th.sorted-th {
   color: #00A0C1 !important;
}

.sort-icon-inactive {
   color: #cbd5e1;
   font-size: 0.72rem;
   margin-left: 4px;
   opacity: 0.45;
   transition: opacity 0.15s ease, color 0.15s ease;
}

.sortable-th:hover .sort-icon-inactive {
   opacity: 0.85;
   color: #94a3b8;
}

.sort-icon-active {
   color: #00A0C1 !important;
   font-size: 0.75rem;
   margin-left: 4px;
}

.modern-row {
   cursor: pointer;
   transition: background-color 0.15s ease;
}

.modern-row:hover {
   background-color: #f8fafc;
}

.modern-row td {
   padding: 12px 16px !important;
   border-bottom: 1px solid #f1f5f9;
   vertical-align: middle;
}

.contract-org-name {
   font-weight: 700;
   font-size: 0.92rem;
   color: #0f172a;
}

.modern-row:hover .contract-org-name {
   color: #00A0C1;
}

.contract-sub-title {
   font-size: 0.8rem;
}

/* ── Signer Rows & Status Badges ── */
.signer-list-wrapper {
   min-width: 190px;
}

.signer-row {
   padding: 4px 8px;
   border-radius: 6px;
   background: #f8fafc;
   border: 1px solid #f1f5f9;
   transition: all 0.15s ease;
}

.signer-row:hover {
   background: #f1f5f9;
   border-color: #e2e8f0;
}

.fs-xxs {
   font-size: 0.68rem !important;
}

.bg-emerald-soft {
   background-color: #ecfdf5 !important;
   color: #059669 !important;
   border: 1px solid #a7f3d0;
}

.bg-amber-soft {
   background-color: #fffbeb !important;
   color: #b45309 !important;
   border: 1px solid #fde68a;
}

.bg-slate-soft {
   background-color: #f1f5f9 !important;
   color: #64748b !important;
   border: 1px solid #cbd5e1;
}

/* ── Spark Soft Pill Badges (Matching Leads V2) ── */
.spark-pill-badge {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 6px;
   padding: 4px 10px;
   border-radius: 14px;
   font-size: 0.8125rem;
   font-weight: 600;
   white-space: nowrap;
   line-height: 1.35;
   letter-spacing: 0.2px;
   text-decoration: none;
   vertical-align: middle;
   border: 1px solid transparent;
   transition: all 0.15s ease-in-out;
}

.spark-pill-badge i {
   font-size: 0.78rem;
}

.spark-pill-badge--sent { background: #ecfeff; color: #008298; border-color: #cffafe; }
.spark-pill-badge--viewed { background: #e0f2fe; color: #0369a1; border-color: #bae6fd; }
.spark-pill-badge--review { background: #fffbeb; color: #b45309; border-color: #fde68a; }
.spark-pill-badge--executed { background: #ecfdf5; color: #059669; border-color: #a7f3d0; }
.spark-pill-badge--approved { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
.spark-pill-badge--cancelled { background: #fee2e2; color: #b91c1c; border-color: #fca5a5; }
.spark-pill-badge--declined { background: #ffe4e6; color: #be123c; border-color: #fecdd3; }
.spark-pill-badge--expired { background: #fee2e2; color: #b91c1c; border-color: #fca5a5; }
.spark-pill-badge--pending-approval { background: #ffedd5; color: #c2410c; border-color: #fed7aa; }
.spark-pill-badge--draft { background: #f8fafc; color: #475569; border-color: #e2e8f0; }

.spark-pill-badge--multi-signer { background: #f3e8ff; color: #6b21a8; border-color: #e9d5ff; }
.spark-pill-badge--revision-pending { background: #fff1f2 !important; color: #e11d48 !important; border: 1px solid #fecdd3 !important; }
.spark-pill-badge--unsent { background: #fffbeb; color: #b45309; border-color: #fde68a; font-size: 0.7rem; padding: 2px 7px; }
.spark-pill-badge--unsent-edits { background: #fffbeb !important; color: #b45309 !important; border: 1px solid #fde68a !important; font-size: 0.7rem !important; padding: 2px 7px !important; }
.spark-pill-badge--unresolved { background: #fef2f2 !important; color: #dc2626 !important; border: 1px solid #fecaca !important; font-size: 0.7rem !important; padding: 2px 7px !important; }
.spark-pill-badge--revision { background: #e0e7ff; color: #3730a3; border-color: #c7d2fe; font-size: 0.7rem; padding: 2px 7px; }
.spark-pill-badge--version-major { background: #ecfdf5 !important; color: #059669 !important; border: 1px solid #a7f3d0 !important; }
.spark-pill-badge--version-minor { background: #fff7ed !important; color: #c2410c !important; border: 1px solid #fed7aa !important; }
.spark-pill-badge--version-default { background: #f1f5f9 !important; color: #475569 !important; border: 1px solid #cbd5e1 !important; }

/* ── Row Accent Border Indicators ── */
.row-border-revision {
   border-left: 3.5px solid #f43f5e !important;
   padding-left: 14px !important;
}

.row-border-multi-signer {
   border-left: 3.5px solid #8b5cf6 !important;
   padding-left: 14px !important;
}

.badge-revision-dot {
   position: absolute;
   top: -3px;
   right: -3px;
   width: 8px;
   height: 8px;
   border-radius: 50%;
   background-color: #f43f5e;
   border: 1.5px solid #ffffff;
}

/* ── Streamlined Action Buttons (Matching LeadV2) ── */
.btn-action-send {
   height: 32px;
   padding: 0 12px;
   font-size: 0.8rem;
   font-weight: 600;
   border-radius: 6px;
   background: #e6f7fa;
   color: #008298;
   border: 1px solid rgba(0, 160, 193, 0.2);
   display: inline-flex;
   align-items: center;
   transition: all 0.15s ease;
}

.btn-action-send:hover:not(:disabled) {
   background: #00A0C1;
   color: #ffffff;
   box-shadow: 0 2px 6px rgba(0, 160, 193, 0.25);
}

.btn-action-send--unsent {
   background: #fffbeb !important;
   color: #b45309 !important;
   border: 1px solid #fde68a !important;
   font-weight: 700 !important;
}

.btn-action-send--unsent:hover:not(:disabled) {
   background: #fef3c7 !important;
   color: #92400e !important;
   border-color: #fcd34d !important;
   box-shadow: 0 2px 6px rgba(245, 158, 11, 0.25) !important;
}

.badge-unsent-dot {
   position: absolute;
   top: 2px;
   right: 2px;
   width: 8px;
   height: 8px;
   border-radius: 50%;
   background-color: #f59e0b;
   border: 1.5px solid #ffffff;
}

.btn-action-send:disabled {
   opacity: 0.4;
   cursor: not-allowed;
}

.btn-more-actions {
   width: 34px;
   height: 34px;
   min-width: 34px;
   min-height: 34px;
   padding: 0;
   border-radius: 50% !important;
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   color: #64748b;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.85rem;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
}

.btn-more-actions:hover {
   background: #f1f5f9;
   color: #00A0C1;
   border-color: #cbd5e1;
   transform: translateY(-1px);
   box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.modern-action-dropdown {
   min-width: 220px;
   border-radius: 10px;
}

.modern-action-dropdown .dropdown-item {
   padding: 8px 16px;
   font-size: 0.85rem;
   font-weight: 500;
   color: #334155;
   transition: all 0.15s ease;
}

.modern-action-dropdown .dropdown-item:hover {
   background-color: #f8fafc;
   color: #00A0C1;
}

/* ── Circular Action Buttons ── */
.action-btn-circle {
   width: 34px;
   height: 34px;
   min-width: 34px;
   min-height: 34px;
   padding: 0;
   border-radius: 50% !important;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.85rem;
   border: none;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
   text-decoration: none;
   position: relative;
}

.action-btn-circle:hover:not(:disabled) {
   transform: translateY(-1px);
}

.action-btn-circle:disabled,
.action-btn-circle.disabled {
   opacity: 0.35;
   cursor: not-allowed;
   pointer-events: none;
   transform: none !important;
   box-shadow: none !important;
}

.action-btn-circle--primary,
.action-btn-circle--proposal,
.action-btn-circle--send,
.action-btn-circle--edit {
   background-color: #e6f7fa;
   color: #00A0C1;
}

.action-btn-circle--primary:hover:not(:disabled),
.action-btn-circle--proposal:hover:not(:disabled),
.action-btn-circle--send:hover:not(:disabled),
.action-btn-circle--edit:hover:not(:disabled) {
   background-color: #00A0C1;
   color: #ffffff;
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}

.action-btn-circle--sync {
   background-color: #f0fdfa;
   color: #0d9488;
}

.action-btn-circle--sync:hover:not(:disabled) {
   background-color: #0d9488;
   color: #ffffff;
   box-shadow: 0 4px 10px rgba(13, 148, 136, 0.3);
}

.action-btn-circle--restore {
   background-color: #fffbeb;
   color: #f59e0b;
}

.action-btn-circle--restore:hover:not(:disabled) {
   background-color: #f59e0b;
   color: #ffffff;
   box-shadow: 0 4px 10px rgba(245, 158, 11, 0.3);
}

.action-btn-circle--unsent {
   background-color: #fffbeb;
   color: #b45309;
}

.action-btn-circle--unsent:hover:not(:disabled) {
   background-color: #f59e0b;
   color: #ffffff;
   box-shadow: 0 4px 10px rgba(245, 158, 11, 0.3);
}

.action-icon-btn--danger:hover:not(:disabled) {
   background: #fee2e2 !important;
   color: #ef4444 !important;
   border-color: #fca5a5 !important;
}

.notification-dot {
   position: absolute;
   top: 2px;
   right: 2px;
   width: 8px;
   height: 8px;
   background: #ef4444;
   border-radius: 50%;
   border: 1.5px solid #ffffff;
}

/* Status dots */
.status-dot {
   width: 8px;
   height: 8px;
   border-radius: 50%;
}
.status-dot-xs {
   width: 6px;
   height: 6px;
   border-radius: 50%;
   display: inline-block;
}
.status-dot--teal { background-color: #00A0C1; }
.status-dot--emerald { background-color: #10b981; }
.status-dot--amber { background-color: #f59e0b; }
.status-dot--indigo { background-color: #6366f1; }
.status-dot--rose { background-color: #ef4444; }

.modern-checkbox-label {
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 6px 8px;
   border-radius: 6px;
   cursor: pointer;
   font-size: 0.8125rem;
   color: #334155;
   transition: background 0.15s ease;
   user-select: none;
   position: relative;
   margin-bottom: 0;

   &:hover {
      background: #f1f5f9;
   }

   input[type="checkbox"] {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
   }

   .custom-chk {
      width: 16px;
      height: 16px;
      background-color: #ffffff;
      border: 1.5px solid #cbd5e1;
      border-radius: 4px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
      flex-shrink: 0;
      position: relative;
   }

   &:hover input[type="checkbox"]:not(:checked) ~ .custom-chk {
      border-color: #00A0C1;
   }

   input[type="checkbox"]:checked ~ .custom-chk {
      background-color: #00A0C1;
      border-color: #00A0C1;
   }

   input[type="checkbox"]:checked ~ .custom-chk::after {
      content: "";
      width: 4px;
      height: 8px;
      border: solid #ffffff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      margin-bottom: 1px;
   }

   input[type="checkbox"]:focus ~ .custom-chk {
      box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.2);
   }
}

/* ── Pagination Modern ── */
.pagination-modern {
   display: inline-flex;
   align-items: center;
   border: 1px solid #cbd5e1;
   border-radius: 8px;
   background: #ffffff;
   overflow: hidden;
}

.pagination-arrow {
   background: transparent;
   border: none;
   padding: 6px 12px;
   color: #00A0C1;
   cursor: pointer;
   font-size: 0.8rem;
   transition: background 0.15s ease;
}

.pagination-arrow:hover:not(:disabled) {
   background: #f1f5f9;
}

.pagination-arrow:disabled {
   color: #cbd5e1;
   cursor: not-allowed;
}

.pagination-label {
   font-size: 0.8rem;
   font-weight: 500;
   color: #64748b;
   padding: 0 8px;
   border-left: 1px solid #f1f5f9;
   border-right: 1px solid #f1f5f9;
}

/* ── Modern Activity Modal Styling ── */
.modal-blur {
   backdrop-filter: blur(4px);
}

.modern-modal-content {
   border-radius: 16px;
   border: none;
   box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
   overflow: hidden;
   background: #ffffff;
}

.modern-activity-modal-header {
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 18px 24px;
   background: #ffffff;
   border-bottom: 1px solid #e2e8f0;
}

.modal-header-icon-badge {
   width: 40px;
   height: 40px;
   border-radius: 10px;
   background: rgba(0, 160, 193, 0.1);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.1rem;
   color: #00A0C1;
   flex-shrink: 0;
}

.modal-header-icon-badge i {
   color: #00A0C1 !important;
}

.modern-activity-modal-header .modal-title {
   font-size: 1.05rem;
   color: #1e293b;
}

.modern-activity-modal-header .modal-subtitle {
   font-size: 0.8rem;
   color: #64748b;
}

.modern-modal-close-btn {
   width: 32px;
   height: 32px;
   border-radius: 8px;
   border: none;
   background: #f1f5f9;
   color: #64748b;
   display: flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;
   transition: all 0.15s ease;
}

.modern-modal-close-btn:hover {
   background: #e2e8f0;
   color: #1e293b;
}

.modern-activity-modal-body {
   background: #ffffff;
   max-height: 70vh;
   overflow-y: auto;
}

.modern-activity-modal-footer {
   padding: 12px 24px;
   background: #f8fafc;
   border-top: 1px solid #e2e8f0;
   display: flex;
   justify-content: flex-end;
}
</style>
